// ── A client's name is complete before staff raise a document for them (Calem, Bugify #be42fe7f) ────
// Frontend mirror of server/src/services/clientNameGap.ts — keep the two in step; the server's refusal is the real
// gate, and this says it sooner, in the reader's language.
//   individual  Title, First name and Last name, each holding a letter of some script: a box holding only
//               '-', '.' or '_' counts as empty
//   company     its company name — the Thai registered one when the English box is blank. Never asked for a person.
//               A lone mark left at the end ("Jane -") counts as incomplete.
import { api } from './api';

export type NameBox = 'title' | 'first' | 'last' | 'company';
// The boxes in the words the card uses; 'Title' on its own would not match the box the card labels 'Prefix'.
const BOX_KEY: Record<NameBox, string> = { title: 'doc_name_box_title', first: 'cl_nf', last: 'cl_nl', company: 'ct_company_name' };

const s = (v: any): string => String(v == null ? '' : v).trim();
const HAS_LETTER = /\p{L}/u;
const HAS_WORD = /[\p{L}\p{N}]/u;   // a company may be all digits; a person's box may not
const ENDS_ON_LONE_MARK = /(?:^|\s)[\p{Pd}_.]+$/u;

/** The boxes a natural person's name still lacks. */
export function personNameGap(prefix: any, first: any, last: any): NameBox[] {
  const gap: NameBox[] = [];
  if (!HAS_LETTER.test(s(prefix))) gap.push('title');
  if (!HAS_LETTER.test(s(first))) gap.push('first');
  if (!HAS_LETTER.test(s(last))) gap.push('last');
  return gap;
}

/** A company name that would not do: none at all, marks only, or a lone mark left at its end. */
export function companyNameGap(name: any): NameBox[] {
  const n = s(name);
  return HAS_WORD.test(n) && !ENDS_ON_LONE_MARK.test(n) ? [] : ['company'];
}

/** The boxes this client row still lacks; empty = its name is complete. */
export function clientNameGap(c: any): NameBox[] {
  if (s(c?.entity_type) === 'individual') return personNameGap(c?.name_prefix, c?.name_first, c?.name_last);
  return companyNameGap(s(c?.company_name) || s(c?.company_name_th));
}

/** The four parts as one comparable string: a card is asked for a complete name only when a save changes them. Case
 *  is ignored, because a person's card opens with a name saved in capitals already corrected. */
export const namePartsKey = (prefix: any, first: any, middle: any, last: any): string =>
  [prefix, first, middle, last].map(v => s(v).toLowerCase()).join('|');

/** The picked client's gap, read by id and afresh, so a card corrected in another tab counts straight away. A read
 *  that fails says nothing: the server's own check then decides. */
export const readClientGap = (clientId: string): Promise<NameBox[]> =>
  api.getClient(clientId).then((c: any) => clientNameGap(c)).catch(() => []);

/** The refusal a document form shows, naming the boxes the client's card lacks. */
export const docNameGapText = (t: any, boxes: NameBox[]): string =>
  t('doc_name_gap', { boxes: boxes.map(b => t(BOX_KEY[b])).join(', ') });
